import { EraQlAst } from '../eraql-ast/ast';
import { Indent, Line, FormatNode, Nodes, Text } from './Node';
import { assertExists } from '@cotera/utilities';
import { infixOperatorBindingPower } from '../eraql-ast/binding-power';
import { buildNode } from './Builder';
import { WithMeta } from '../lex';

/**
 * Builds operator expressions; only inserting parens as necessary.
 *
 * Examples (LHS is parens implied by AST, RHS is expected render):
 * 1. (5 + 6) + 7 => 5 + 6 + 7    (parens redudnant)
 * 2. 5 + (6 + 7) => 5 + (6 + 7)  (parens needed because they specify a different order of ops)
 * 3. (5 * 6) + 7 => 5 * 6 + 7    (parens redudnant)
 * 4. 5 * (6 + 7) => 5 * (6 + 7)
 * 5. (5 + 6) * 7 => (5 + 6) * 7
 * 6. 5 + (6 * 7) => 5 + 6 * 7    (parens redundant)
 *
 * Algo:
 *
 * Expr := Number | Expr Op Expr
 *
 * Case Expr:
 * - Number => No parens
 * - Number Op Number => No parens
 * - Number OpX (ExprA OpY ExprB) => Add parens on RHS if OpX left power is > OpY right power
 * - (ExprA OpX ExprB) OpY Number => Add parens on LHS if OpX right power is < OpY left power
 * - (ExprA OpX ExprB) OpY (ExprC OpZ ExprD) => combine logic in above two cases.
 *
 * Examples:
 *
 * 1.
 *
 * expr:    (5 + 6)  +  7
 * power:  0 (8 9) 8   9  0
 *
 * The left + binds stronger to six than the right + does,
 * so parens (which align with that relationship) are redundant
 *
 * 2.
 *
 * expr:   5  +  (6 + 7)
 * power: 0  8  9  (8 9)  0
 *
 * The left + bunds stronger to the 6 than the right + does,
 * so parens (which contradict that relationship) are needed.
 *
 * 3.
 *
 * expr:    (5 * 6)  +  7
 * power: 0 (10 11) 8  9  0
 *
 * The left * binds stronger to six than the right + does,
 * so parens (which align with that relationship) are redundant
 *
 * 4.
 *
 * expr:   5  *  (6 + 7)
 * power: 0 10 11 (8 9)  0
 *
 * The left * binds stronger to six than the right + does,
 * so parens (which contradict with that relationship) are needed
 *
 * 5.
 *
 * expr:   (5 + 6)  * 7
 * power: 0 (8 9) 10 11 0
 *
 * The left + binds weaker to six than the right * does,
 * so parens (which constradict with that relationship) are needed
 *
 * 6.
 *
 * expr:   5 + (6  *  7)
 * power: 0 8 9 (10 11) 0
 *
 * The left + binds weaker to six than the right * does,
 * so parens (which align with that relationship) are redundant
 */
export function buildInfixOperatorCall(
  leftExpr: WithMeta<EraQlAst>,
  rightExpr: WithMeta<EraQlAst>,
  op: string
): FormatNode {
  const leftOperandKind = getOperandExpressionKind(leftExpr[0]);
  const rightOperandKind = getOperandExpressionKind(rightExpr[0]);

  const thisBindingPower = assertExists(infixOperatorBindingPower(op));

  const leftExprNeedsParens =
    leftOperandKind.t === 'operatatorExpression' &&
    leftOperandKind.bindingPower[1] < thisBindingPower[0];

  const rightExprNeedsParens =
    rightOperandKind.t === 'operatatorExpression' &&
    thisBindingPower[1] > rightOperandKind.bindingPower[0];

  const leftExprNode = leftExprNeedsParens
    ? Nodes([Text('('), buildNode(leftExpr), Text(')')])
    : buildNode(leftExpr);

  const rightExprNode = rightExprNeedsParens
    ? Nodes([Text('('), buildNode(rightExpr), Text(')')])
    : buildNode(rightExpr);

  // No leading whitespace if operator is semicolon.
  const opNode =
    op === ';'
      ? Nodes([Text(';'), Line()])
      : op === '|>'
      ? Nodes([Line(), Text(`|> `)])
      : Text(` ${op} `);

  return Indent([leftExprNode, opNode, rightExprNode]);
}

type OperandExpressionKind =
  | {
      t: 'nonOperatorExpression';
    }
  | {
      t: 'operatatorExpression';
      bindingPower: [number, number];
    };

function getOperandExpressionKind(operand: EraQlAst): OperandExpressionKind {
  switch (operand.t) {
    case 'call': {
      const [op, _] = operand.op;

      if (op.t !== 'operator') {
        return { t: 'nonOperatorExpression' };
      }

      const bindingPower = assertExists(infixOperatorBindingPower(op.op));

      return {
        t: 'operatatorExpression',
        bindingPower,
      };
    }

    default:
      return { t: 'nonOperatorExpression' };
  }
}
